import { Component } from 'react';
import { KeyValue } from '@mews/enums';
import { ANIMATION_DURATION } from '@optimus-web/common';
import { ActivableRenderer, ActivableRendererProps } from '@optimus-web/helpers';
import { noop } from '../utils/func';
import { scrollTo } from '../utils/scroll';
import { CalendarService } from '../utils/services/createCalendarService';
import {
    MonthsContainerElement,
    ScrollContainerElement,
    SelectableMonthElement,
    YearElement,
    YearLabelElement,
} from './style';

const LOAD_THRESHOLD = 50;
const LOAD_AMOUNT = 10;

interface CalendarMonthSelectorProps<D, M, Y> extends ActivableRendererProps {
    onChange: (event: React.MouseEvent | React.KeyboardEvent<HTMLButtonElement>, value: M) => void;
    numberOfWeeks: number;
    selectedMonth: M;
    currentDate: D;
    getCalendarService: () => CalendarService<D, M, Y>;
    monthSelectorVisible?: boolean;
    monthSelectedPosition?: number;
    isMonthlyPicker?: boolean;
    allowedRange: {
        maxDate?: D;
        minDate?: D;
    };
}

interface CalendarMonthSelectorState<M, Y> {
    years: Array<Y>;
    focusMonth: M;
}

interface MonthHandlers {
    [key: string]: (element: HTMLElement | null) => void;
}

interface MonthElements {
    [key: string]: HTMLElement | null;
}

class CalendarMonthSelector<D, M, Y> extends Component<
    CalendarMonthSelectorProps<D, M, Y>,
    CalendarMonthSelectorState<M, Y>
> {
    static defaultProps = {
        onChange: noop,
    };

    private monthHandlers: MonthHandlers;
    private monthElements: MonthElements;
    private scrollDisabled?: boolean;
    private scrollElement?: HTMLElement | null;

    constructor(props: CalendarMonthSelectorProps<D, M, Y>) {
        super(props);

        const { selectedMonth, getCalendarService } = props;

        const year = getCalendarService().getYearOfMonth(selectedMonth);
        const prevYears = this.getPreviousYears(year);
        const nextYears = this.getNextYears(year);

        this.state = {
            years: [...prevYears, year, ...nextYears],
            focusMonth: selectedMonth,
        };

        this.monthHandlers = {};
        this.monthElements = {};
    }

    componentDidMount() {
        /* This doesnt work with angular, it's hardcoded in style until we get rid of it in commander
        // hide scrollbar in scroll container
        const width = this.scrollElement.scrollWidth;
        this.monthSelectorElement.style.width = `${width}px`;
        */

        if (this.props.selectedMonth) {
            this.scrollToMonth(this.props.selectedMonth, { animated: false });
        }
    }

    componentDidUpdate() {
        if (this.props.selectedMonth) {
            // scroll to selected month in case the input value changes
            this.scrollToMonth(this.props.selectedMonth, { animated: false });
        }
        if (this.scrollElement && this.scrollElement.scrollTop === 0) {
            this.scrollElement.scrollTop = 1;
        }
    }

    scrollToMonth(month: M, { animated = false } = {}) {
        const monthKey = this.props.getCalendarService().getMonthUniqueName(month);
        const monthElement = this.monthElements[monthKey];

        if (monthElement) {
            this.scrollDisabled = true;
            window.requestAnimationFrame(() => {
                if (this.scrollElement) {
                    scrollTo(monthElement, this.scrollElement, {
                        callback: this.scrollToMonthFinished,
                        topOffset: (this.scrollElement.offsetHeight - monthElement.offsetHeight) / 2,
                        ...(animated ? {} : { speed: 0 }),
                    });
                }
            });
        }
    }

    scrollToMonthFinished = () => {
        this.scrollDisabled = false;
        // handle possible scroll to the edge
        this.handleScroll();
    };

    addYearsToTop() {
        const { years } = this.state;

        const firstYear = years[0];
        const prevYears = this.getPreviousYears(firstYear);

        this.setState({ years: [...prevYears, ...years] });
    }

    addYearsToBottom() {
        const { years } = this.state;

        const lastYear = years[years.length - 1];
        const nextYears = this.getNextYears(lastYear);

        this.setState({ years: [...years, ...nextYears] });
    }

    getPreviousYears(year: Y) {
        const { getCalendarService } = this.props;
        const { addYears, getYearName } = getCalendarService();
        const years: Array<Y> = [];

        for (let i = LOAD_AMOUNT; i !== 0; --i) {
            const newYear = addYears(year, -i);

            if (getYearName(newYear) > 0) {
                years.push(newYear);
            }
        }

        return years;
    }

    getNextYears(year: Y) {
        const { getCalendarService } = this.props;
        const years: Array<Y> = [];
        for (let i = 1; i !== LOAD_AMOUNT; ++i) {
            years.push(getCalendarService().addYears(year, i));
        }

        return years;
    }

    handleScroll = () => {
        if (this.scrollDisabled || !this.scrollElement) {
            return;
        }

        const { scrollTop, scrollHeight, offsetHeight } = this.scrollElement;

        if (scrollTop < LOAD_THRESHOLD) {
            this.addYearsToTop();
        } else if (scrollHeight - scrollTop - offsetHeight < LOAD_THRESHOLD) {
            this.addYearsToBottom();
        }
    };

    handleMonthClick = (month: M) => (e: React.MouseEvent) => {
        this.props.onChange(e, month);
    };

    handleScrollContainerRef = (element: HTMLElement | null) => (this.scrollElement = element);

    getMonthRefHandler = (monthKey: string) => {
        if (!this.monthHandlers[monthKey]) {
            this.monthHandlers[monthKey] = (element: HTMLElement | null) => (this.monthElements[monthKey] = element);
        }

        return this.monthHandlers[monthKey];
    };

    setFocusMonth = () => {
        const { getCalendarService } = this.props;
        const { focusMonth } = this.state;
        const { getMonthUniqueName } = getCalendarService();
        const monthName = getMonthUniqueName(focusMonth);
        const monthElement = document.querySelector(`[data-month='${monthName}']`) as HTMLElement;

        if (monthElement) {
            monthElement.focus();
            this.scrollToMonth(focusMonth, { animated: true });
        }
    };

    isMonthDisabled = (month: M) => {
        const { isMonthlyPicker, getCalendarService, allowedRange } = this.props;
        return !!isMonthlyPicker && getCalendarService().isMonthDisabled(month, allowedRange);
    };

    handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        const { getCalendarService } = this.props;
        const { focusMonth } = this.state;
        const { getNextMonth, getPrevMonth, addMonths, subtractMonths } = getCalendarService();

        const prevMonth = getPrevMonth(focusMonth);
        const nextMonth = getNextMonth(focusMonth);
        const aboveMonth = subtractMonths(focusMonth, 6);
        const belowMonth = addMonths(focusMonth, 6);

        let handled;

        switch (e.key) {
            case KeyValue.ArrowUp:
                if (!this.isMonthDisabled(aboveMonth)) {
                    this.setState({ focusMonth: aboveMonth }, this.setFocusMonth);
                }
                handled = true;
                break;

            case KeyValue.ArrowDown:
                if (!this.isMonthDisabled(belowMonth)) {
                    this.setState({ focusMonth: belowMonth }, this.setFocusMonth);
                }
                handled = true;
                break;

            case KeyValue.ArrowLeft:
                if (!this.isMonthDisabled(prevMonth)) {
                    this.setState({ focusMonth: prevMonth }, this.setFocusMonth);
                }
                handled = true;
                break;

            case KeyValue.ArrowRight:
                if (!this.isMonthDisabled(nextMonth)) {
                    this.setState({ focusMonth: nextMonth }, this.setFocusMonth);
                }
                handled = true;
                break;

            case KeyValue.Enter:
                if (!this.isMonthDisabled(focusMonth)) {
                    this.props.onChange(e, focusMonth);
                }

                handled = true;
                break;

            default:
                break;
        }

        if (handled) {
            e.stopPropagation();
            e.preventDefault();
        }
    };

    render() {
        const { selectedMonth, getCalendarService, currentDate, onChange, ...other } = this.props;
        const { years, focusMonth } = this.state;
        const calendarService = getCalendarService();
        const monthShortNames = calendarService.getMonthShortNames();
        const todayMonth = calendarService.getMonthOfDate(currentDate);

        return (
            <ScrollContainerElement ref={this.handleScrollContainerRef} onScroll={this.handleScroll} {...other}>
                {years.map((y) => {
                    const yearName = calendarService.getYearName(y);
                    const months = calendarService.getMonthsOfYear(y);

                    return (
                        <YearElement key={yearName}>
                            <YearLabelElement>{yearName}</YearLabelElement>
                            <MonthsContainerElement>
                                {months.map((m) => {
                                    const monthShortName = monthShortNames[calendarService.getMonthIndex(m)];
                                    const monthName = calendarService.getMonthName(m);
                                    const monthKey = calendarService.getMonthUniqueName(m);
                                    const selected = calendarService.isMonthEqual(m, selectedMonth);
                                    const disabled = this.isMonthDisabled(m);
                                    const currentMonth = calendarService.isMonthEqual(m, todayMonth);
                                    const hasFocus = calendarService.isMonthEqual(m, focusMonth);

                                    return (
                                        <SelectableMonthElement
                                            key={monthKey}
                                            ref={this.getMonthRefHandler(monthKey)}
                                            selected={selected}
                                            disabled={disabled}
                                            currentMonth={currentMonth}
                                            onClick={this.handleMonthClick(m)}
                                            tabIndex={hasFocus ? 0 : -1}
                                            data-month={monthKey}
                                            data-test-month={monthKey}
                                            onKeyDown={this.handleKeyDown}
                                            aria-label={monthName}
                                            variant="ghost"
                                            size="medium"
                                        >
                                            {monthShortName}
                                        </SelectableMonthElement>
                                    );
                                })}
                            </MonthsContainerElement>
                        </YearElement>
                    );
                })}
            </ScrollContainerElement>
        );
    }
}

const CalendarActivator = ActivableRenderer({
    delay: ANIMATION_DURATION.default,
    activePropName: 'monthSelectorVisible',
}) as <D, M, Y>(
    Component: React.ComponentType<CalendarMonthSelectorProps<D, M, Y>>
) => React.ComponentType<CalendarMonthSelectorProps<D, M, Y>>;

const ActivableCalendarMonthSelector = CalendarActivator(CalendarMonthSelector) as <D, M, Y>(
    props: CalendarMonthSelectorProps<D, M, Y>
) => React.ReactElement<CalendarMonthSelectorProps<D, M, Y>>;

export default ActivableCalendarMonthSelector;
